import { FC, useState } from "react";
import AppSearchBox from "../../../components/searchBox";
import { useDebouncedCallback } from "use-debounce";
import { useCreateChatChannelMutation, useSearchChatUsersQuery } from "../../../api/network/chatApiServices";
import svgIcons from "../../../assets/svg/svgIcons";
import ChatListItem from "./chatListItem";
import { useTranslation } from "react-i18next";
import { useChatContext } from "../contexts/chat.context";
import { APP_CONFIG } from "../../../constants/constants";
import { ChatSearchedUser } from "../../../api/types/Chat";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../../api/types/Onboarding";
import { useDispatch, useSelector } from "react-redux";
import { TPubNubFile, TPubNubTextMessage } from "../contexts/types/pubnubChat";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import MessageModeSelector from "./messageModeSelector";
import { pubnubTimetokenToJsTime } from "../contexts/util/chat";

type TChatAsideColumnProps = {
  wrapperClass?: string;
}

const ChatAsideColumn: FC<TChatAsideColumnProps> = ({
  wrapperClass = "col-span-4 flex flex-col h-[calc(100vh-160px)]",
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'chatSection' });
  const loggedInUserData = useSelector((state: { commonReducer: { user: LegacyVerifyEmailOtpResponseSuccess } }) => state.commonReducer.user);
  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);
  const dispatch = useDispatch();

  const {
    subscribedChannels,
    selectedChatChannel,
    setSelectedChatChannel,
    channelTransitionInProgress,
    setChannelTransitionInProgress,
    conversations,
    presence,
  } = useChatContext();

  const [searchKeyword, setSearchKeyword] = useState<string>('');

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    setSearchKeyword(value);
  }, 500);

  const {
    data: dataSearchChatUsers,
    isFetching: isFetchingSearchChatUsers,
  } = useSearchChatUsersQuery(
    { search: searchKeyword },
    { skip: !searchKeyword }
  )

  const [createChatChannelAPITrigger, { isLoading: isLoadingCreateChatChannel }] = useCreateChatChannelMutation();
  const handleChatUserClick = (user: ChatSearchedUser) => {
    setSelectedChatChannel("");
    // if a channel already exists, select it
    const existingChannel
      = subscribedChannels
        .filter(channel => channel.members_info?.length === 2) // filter only 1-1 channels
        .find(
          channel =>
            channel.members_info
              ?.find(member => member.user_id === user.userid)
        );
    if (!!existingChannel) {
      debouncedSetSearchKeyword("");
      setSelectedChatChannel(existingChannel.channel_id);
      return;
    }
    // else create a new channel
    createChatChannelAPITrigger(
      {
        channel_type: 1,
        members: [user.userid],
      }
    )
      .unwrap()
      .then((response) => {
        debouncedSetSearchKeyword("");
        setSearchText("");
        setSelectedChatChannel(response?.channel_id);
      })
      .catch((error) => {
        console.error('error:', error);
      });
  }

  const handleChannelClick = (channelId: string) => {
    if (channelId === selectedChatChannel) return;
    setSelectedChatChannel(channelId);
  }

  const [searchText, setSearchText] = useState<string>('');

  return (
    <div className={`relative ${wrapperClass}`}>

      {/* Messaging Mode Selector */}
      <MessageModeSelector />

      {/* Search Box */}
      <AppSearchBox
        key={`chat-search-box-${selectedChatChannel}`}
        controlledValue={searchText}
        initialValue={searchKeyword}
        placeholder={t('search_placeholder')}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          debouncedSetSearchKeyword(e.target.value)
          setSearchText(e.target.value)
        }}
        onClear={() => {
          debouncedSetSearchKeyword("")
          setSearchText("")
        }}
        wrapperClassName="w-full mb-4"
        inputClassName="w-full rounded-md border-gray-200 py-2.5 ps-10 shadow-sm sm:text-sm enabled:outline-accent-blue-dark"
      />

      {/* Items List */}
      {isFetchingSearchChatUsers ? (
        <div className="flex-grow flex items-center justify-center">
          <img src={svgIcons.LoadingAnimation} alt="Loading animation" className="size-16" />
        </div>
      ) : (
        <ul className={`flex-grow flex flex-col overflow-y-auto${isLoadingCreateChatChannel ? ' opacity-50 grayscale pointer-events-none touch-none' : ''}`}>
          {!!searchKeyword ? (
            <>
              {/* user search results */}
              {dataSearchChatUsers?.results.length === 0 ? (
                <li>
                  <p className="font-bold text-heading-gray888/70 py-4 text-center">
                    {t('no_users_found')}
                  </p>
                </li>
              ) : (
                <>
                  {dataSearchChatUsers?.results?.map((searchedUser, index) => (
                    <ChatListItem
                      key={index}
                      initials={!!searchedUser?.contactname
                        ? searchedUser?.contactname?.split(' ').map((name: string) => name[0]).join('')
                        : searchedUser?.userid?.substring(0, 2)|| ''
                      }
                      isFirstItem={index === 0}
                      lastMessage={searchedUser?.userid}
                      onClick={() => handleChatUserClick(searchedUser)}
                      title={!!searchedUser?.contactname ? searchedUser?.contactname : searchedUser?.userid}
                    />
                  ))}
                </>
              )}
            </>
          ) : (
            <>
              {/* subscribed channels */}
              {subscribedChannels.length === 0 ? (
                <li>
                  <p className="font-bold text-heading-gray888/70 py-4 text-center">
                    {t('no_channels_found')}
                  </p>
                </li>
              ) : (
                <>
                  {/* subscribed channels from backend */}
                  {subscribedChannels.length > 0 && (
                    <>
                      {[...subscribedChannels] // clone the array for sorting
                        // sort by unread cound and last message time
                        .sort((a, b) => {
                          const unreadMessageCountA = presence?.[a.channel_id]?.unreadMessageCount ?? 0; // TODO: need to refactor and use unreadMessageCount from last message
                          const lastMessageTimeA = conversations[a.channel_id]?.length > 0 // && unreadMessageCountA > 0 
                            ? pubnubTimetokenToJsTime(conversations[a.channel_id][conversations[a.channel_id].length - 1].timetoken)
                            : 0;
                          const unreadMessageCountB = presence?.[b.channel_id]?.unreadMessageCount ?? 0; // TODO: need to refactor and use unreadMessageCount from last message
                          const lastMessageTimeB = conversations[b.channel_id]?.length > 0 // && unreadMessageCountB > 0
                            ? pubnubTimetokenToJsTime(conversations[b.channel_id][conversations[b.channel_id].length - 1].timetoken)
                            : 0;
                          if (unreadMessageCountA === unreadMessageCountB) {
                            return lastMessageTimeB - lastMessageTimeA;
                          }
                          return unreadMessageCountB - unreadMessageCountA;
                        })
                        // render
                        .map((channel, index) => (
                          <ChatListItem
                            key={index}
                            channelId={channel.channel_id}
                            initials={!!channel.display_info?.name && channel.display_info?.name.length >= 2
                              ? channel.display_info?.name.split(' ').slice(0, 2).map((name: string) => name[0]).join('')
                              : channel.display_info?.user_id?.substring(0, 2)|| ''
                            }
                            isFirstItem={index === 0}
                            lastMessage={
                              conversations[channel.channel_id]?.length > 0
                                ? (conversations[channel.channel_id][conversations[channel.channel_id].length - 1].entry as TPubNubFile)?.message?.message
                                  ? ((conversations[channel.channel_id][conversations[channel.channel_id].length - 1].entry as TPubNubFile)?.message?.filetype.startsWith('image') ? t('shared_an_image') : t('shared_a_file'))
                                  : (conversations[channel.channel_id][conversations[channel.channel_id].length - 1].entry as TPubNubTextMessage)?.message
                                : undefined
                            }
                            onClick={() => handleChannelClick(channel.channel_id)}
                            selected={selectedChatChannel === channel.channel_id}
                            title={channel.display_info.name}
                            unreadCount={presence?.[channel.channel_id]?.unreadMessageCount ?? 0}
                          />
                        ))}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </ul>
      )}

      {/* Create Group Chat Button */}
      <button
        type="button"
        className={
          "absolute bottom-4 right-4 rounded-lg border transition group p-2 cursor-pointer " +
          "border-accent-blue-pale bg-accent-blue-pale text-accent-blue-dark enabled:hover:bg-accent-blue-dark enabled:hover:text-white active:bg-accent-blue-dark enabled:focus:bg-accent-blue-dark enabled:focus:outline enabled:focus:-outline-offset-1 enabled:focus:outline-accent-blue-dark ring-accent-blue-pale"
        }
        onClick={() => {
          dispatch(setModalsData({ ...modalsState, showCreateGroupChatModal: true }));
        }}
      >
        <img
          className="size-8 transition-all group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert"
          src={svgIcons.UsersIcon}
          alt="create group chat"
        />
      </button>
    </div>
  )
}
export default ChatAsideColumn;